import React, { useRef, useState } from "react";
import classNames from "classnames";

import AccordeonContenu from "@/ui/accordeonContenu";
import Bouton, { VARIANT } from "@/ui/bouton";

import FiltreCheckbox from "./checkbox";
import { FiltreDropdown, FiltreDropdownButton } from "./dropdown";
import FiltreModal from "./modal";
import FiltrePlus from "./plus";
import { FiltreTagListe, FitreTagItem } from "./tag";

import useOnClickOutside from "@/hooks/useOnClickOutside";
import useFilters from "@/hooks/useFilters";

import type { Filtre } from "@/types/filtres";

import styles from "./filtres.module.scss";

type FiltresProps = {
    className?: string;
    filtersCounts: Array<{
        id: number;
        count: number;
    }>;
    total: number;
};

const Filtres = ({ className = "", filtersCounts, total }: FiltresProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [dropdownSelected, setDropdownSelected] = useState<Filtre>();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);

    const {
        filters,
        filtersSelected,
        handleFilterSelection,
        handleFilterRemoval,
        handleFilterClear,
    } = useFilters();

    const handleModal = () => {
        setIsModalOpen((prev) => !prev);
    };
    const handleDropdownSelection = (filtre: Filtre) => {
        if (filtre === dropdownSelected) {
            setDropdownSelected(undefined);
        } else {
            setDropdownSelected(filtre);
        }
    };

    const handleCloseDropdown = () => {
        setDropdownSelected(undefined);
    };

    useOnClickOutside([dropdownRef, dropdownButtonRef], handleCloseDropdown);

    return (
        filters &&
        filters.length > 0 && (
            <div
                data-component="ListeFiltres"
                className={classNames(styles.listeFiltres, {
                    [className]: className,
                })}
            >
                <div className={styles.blockContainer}>
                    <div className={styles.blockPrimary}>
                        {filters.slice(0, 4).map((filtre) => {
                            return (
                                <div key={`filtre-${filtre.id}`}>
                                    <FiltreDropdownButton
                                        filtre={filtre}
                                        onClick={() => handleDropdownSelection(filtre)}
                                        ref={dropdownButtonRef}
                                        isOpen={dropdownSelected === filtre}
                                        selectedCount={
                                            filtersSelected.intersection(new Set(filtre.items)).size
                                        }
                                    >
                                        {dropdownSelected === filtre && (
                                            <FiltreDropdown
                                                handleClear={handleFilterClear}
                                                handleClose={handleCloseDropdown}
                                                total={total}
                                                ref={dropdownRef}
                                            >
                                                <div className={styles.listeFiltreContainer}>
                                                    {filtre.items &&
                                                        filtre.items.map((item) => (
                                                            <FiltreCheckbox
                                                                key={`filtredropdown-${filtre.id}-${item.id}`}
                                                                item={item}
                                                                filtersCounts={filtersCounts}
                                                                filtersSelected={filtersSelected}
                                                                onChange={handleFilterSelection}
                                                            />
                                                        ))}
                                                </div>
                                            </FiltreDropdown>
                                        )}
                                    </FiltreDropdownButton>
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.blockSecondary}>
                        <Bouton
                            className={styles.btnPlusMobile}
                            icon="filtres"
                            onClick={handleModal}
                            variant={VARIANT.BORDER}
                        >
                            Filtrer la recherche
                        </Bouton>
                        {filters.length > 3 && (
                            <Bouton
                                className={styles.btnPlus}
                                color="dark"
                                count={filtersSelected.size}
                                icon="filtres"
                                onClick={handleModal}
                                variant={VARIANT.LINK}
                            >
                                Plus
                            </Bouton>
                        )}
                        <FiltreModal
                            handleClear={handleFilterClear}
                            isOpen={isModalOpen}
                            handleClose={handleModal}
                            total={total}
                        >
                            {filters
                                .filter((filtre) => filtre.items && filtre.items.length > 0)
                                .map((filtre, index: number) => {
                                    const { id: filtreListId, nom, items } = filtre;
                                    const listCount = filtersSelected.intersection(
                                        new Set(filtre.items),
                                    ).size;
                                    const titreAccordeon = `${nom} [${listCount}]`;
                                    return (
                                        <AccordeonContenu
                                            className={styles.accordeonContenu}
                                            id={filtreListId}
                                            isOpen={index === 0}
                                            key={filtreListId}
                                            titre={titreAccordeon}
                                        >
                                            <FiltrePlus>
                                                {items &&
                                                    items.map((item) => (
                                                        <FiltreCheckbox
                                                            key={`filtremodal-${filtreListId}-${item.id}`}
                                                            item={item}
                                                            filtersCounts={filtersCounts}
                                                            onChange={handleFilterSelection}
                                                            filtersSelected={filtersSelected}
                                                        />
                                                    ))}
                                            </FiltrePlus>
                                        </AccordeonContenu>
                                    );
                                })}
                        </FiltreModal>
                    </div>
                </div>
                {filtersSelected && filtersSelected.size > 0 && (
                    <div className={styles.blockThird}>
                        <div className={styles.blockCountTotalResults}>
                            {total} résultat
                            {total > 1 ? "s" : ""}
                        </div>
                        <FiltreTagListe>
                            {Array.from(filtersSelected).map((filtre) => (
                                <FitreTagItem
                                    key={`tag-${filtre.id}`}
                                    filtre={filtre}
                                    onClick={handleFilterRemoval}
                                />
                            ))}
                        </FiltreTagListe>
                    </div>
                )}
            </div>
        )
    );
};

export default Filtres;
